import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import { AppButton, Card, Field, Footer, ScreenHeader, SectionTitle, Stepper } from "@/components/AppUI";
import { Slots } from "@/pages/Cart";
import { SIZE_LABELS, euro, formatError, papi } from "@/lib/pizzeria";

export default function ChangeRequestPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { products } = useShop();
  const [o, setO] = useState(null);
  const [lines, setLines] = useState([]);
  const [slot, setSlot] = useState("");
  const [slots, setSlots] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    papi.get(`/orders/${id}`).then(({ data }) => {
      if (!["nuovo", "in_preparazione"].includes(data.status) || data.change_request?.status === "pending") {
        toast.error(data.change_request?.status === "pending" ? "C'è già una richiesta di modifica in attesa" : "Questo ordine non può più essere modificato");
        nav(`/order/${id}`, { replace: true });
        return;
      }
      setO(data);
      setNotes(data.notes || "");
      setSlot(data.pickup_time);
      setLines((data.items || []).map((x, i) => ({ ...x, key: i })));
    }).catch(() => nav(`/order/${id}`, { replace: true }));
  }, [id, nav]);

  const teglie = lines.reduce((a, x) => a + (x.size === "teglia" ? x.quantity : x.size === "mezza" ? x.quantity * 0.5 : 0), 0);
  const rotonde = lines.reduce((a, x) => a + (x.size === "rotonda" ? x.quantity : 0), 0);
  const loadSlots = () => papi.get("/slots", { params: { teglie, rotonde } }).then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  useEffect(() => {
    if (o) {
      loadSlots();
    }
  }, [o, teglie, rotonde]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!o) return null;
  const setQty = (key, q) => setLines((ls) => (q <= 0 ? ls.filter((x) => x.key !== key) : ls.map((x) => (x.key === key ? { ...x, quantity: q } : x))));
  const known = (x) => x.product_id === "builder" || (products || []).some((p) => p.id === x.product_id);

  const submit = async () => {
    if (!lines.length) return toast.error("L'ordine deve contenere almeno un prodotto. Per annullarlo chiama la pizzeria.");
    if (!slot) return toast.error("Seleziona un orario di ritiro");
    setBusy(true);
    try {
      await papi.post(`/orders/${id}/change-request`, {
        pickup_time: slot, notes: notes.trim(),
        items: lines.filter(known).map((x) => ({ product_id: x.product_id, size: x.size, quantity: x.quantity, extras: x.extras || [], ...(x.builder ? { builder: x.builder } : { removed: x.removed || [] }) })),
      });
      toast.success("Richiesta inviata: la pizzeria ti risponderà a breve");
      nav(`/order/${id}`, { replace: true });
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const slotList = slots && !slots.some((s) => s.value === o.pickup_time) ? [{ value: o.pickup_time, label: new Date(o.pickup_time).toLocaleTimeString("it-IT", { timeZone: "Europe/Rome", hour: "2-digit", minute: "2-digit" }), day: "Orario attuale" }, ...slots] : slots;
  return (
    <div className="flex-1 flex flex-col" data-testid="change-request-page">
      <ScreenHeader title={`Modifica ordine #${o.code}`} fallback={`/order/${id}`} />
      <div className="flex-1 p-4 pb-8 space-y-6">
        <p className="text-sm" style={{ color: "var(--muted-c)" }}>Cambia quantità, orario o note. La pizzeria deve confermare la modifica.</p>
        <Card className="px-3">
          {lines.map((x, i) => (
            <div key={x.key} className="flex items-center gap-3 py-3" style={i ? { borderTop: "1px solid var(--border-c)" } : {}} data-testid={`change-item-${i}`}>
              <div className="flex-1 min-w-0"><p className="font-medium">{x.name}{x.size ? ` · ${SIZE_LABELS[x.size]}` : ""}</p>{x.ingredients && <p className="text-xs" style={{ color: "var(--muted-c)" }}>{x.ingredients}</p>}<p className="text-sm">{euro((x.unit_price || 0) * x.quantity)}</p></div>
              <Stepper value={x.quantity} onChange={(q) => setQty(x.key, q)} testIdPrefix={`change-qty-${i}`} trash />
            </div>
          ))}
        </Card>
        <div><SectionTitle>Orario di ritiro</SectionTitle><Slots slots={slotList} value={slot} onChange={setSlot} retry={loadSlots} /></div>
        <Field label="Note" testId="change-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} multiline />
      </div>
      <Footer><AppButton icon={Send} title="Invia richiesta di modifica" loading={busy} onClick={submit} testId="change-submit-button" className="w-full" /></Footer>
    </div>
  );
}
